import { UIPlugin } from "@uppy/core";
import dataURItoBlob from "@uppy/utils/lib/dataURItoBlob";
import isObjectURL from "@uppy/utils/lib/isObjectURL";

const isVideoPreviewSupported = (fileType) => {
  const mineType = fileType.split("/")[0];
  return mineType === "video";
};

/**
 * The Video Thumbnail Generator plugin
 */

class VideoThumbnailGenerator extends UIPlugin {
  static VERSION = "0.0.0";

  constructor(uppy, opts) {
    super(uppy, opts);
    this.type = "modifier";
    this.id = this.opts.id || "VideoThumbnailGenerator";
    this.title = "Video Thumbnail Generator";
    this.queue = [];
    this.queueProcessing = false;
    this.defaultThumbnailDimension = 200;
    this.thumbnailType = this.opts.thumbnailType || "image/jpeg";

    this.defaultLocale = {
      strings: {
        generatingThumbnails: "Generating thumbnails...",
      },
    };

    const defaultOptions = {
      thumbnailWidth: null,
      thumbnailHeight: null,
      waitForThumbnailsBeforeUpload: false,
      lazy: false,
    };

    this.opts = { ...defaultOptions, ...opts };
    this.i18nInit();

    if (this.opts.lazy && this.opts.waitForThumbnailsBeforeUpload) {
      // eslint-disable-next-line max-len
      throw new Error(
        "VideoThumbnailGenerator: The `lazy` and `waitForThumbnailsBeforeUpload` options are mutually exclusive. Please ensure at most one of them is set to `true`."
      );
    }
  }

  /**
   * C  reate a thumbnail for the given Uppy file object.
   *
   * @param {{data: Blob}} file
   * @param {number} targetWidth
   * @param {number} targetHeight
   * @returns {Promise}
   */
  createThumbnail(file, targetWidth, targetHeight) {
    // bug in the compatibility data
    const originalUrl = URL.createObjectURL(file.data);

    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.setAttribute("controls", true);
      video.setAttribute("muted", "");
      video.setAttribute("preload", "metadata");
      video.setAttribute("crossorigin", "*");
      video.setAttribute("src", originalUrl);
      video.addEventListener("loadedmetadata", () => {
        video.currentTime = Math.floor(video.duration / 2);
      });
      video.addEventListener("timeupdate", () => {
        // bug in the compatibility data
        URL.revokeObjectURL(originalUrl);
        resolve(video);
      });
      video.addEventListener("error", (event) => {
        // bug in the compatibility data
        URL.revokeObjectURL(originalUrl);
        reject(event.error || new Error("Could not create thumbnail"));
      });
    })
      .then((video) => {
        const dimensions = this.getProportionalDimensions(
          video,
          targetWidth,
          targetHeight
        );
        const resizedImage = this.resizeVideo(
          video,
          dimensions.width,
          dimensions.height
        );
        return this.canvasToBlob(resizedImage, this.thumbnailType, 80);
      })
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                preview: URL.createObjectURL(blob),
                previewData: reader.result,
              });
            };
            reader.onerror = (event) => {
              reject(event.error || new Error("Could not create thumbnail"));
            };
            reader.readAsDataURL(blob);
          })
      );
  }

  /**
   * Get the new calculated dimensions for the given video and a target width
   * or height. If both width and height are given, only width is taken into
   * account. If neither width nor height are given, the default dimension
   * is used.
   */
  getProportionalDimensions(video, width, height) {
    const aspect = video.videoWidth / video.videoHeight;

    if (width != null) {
      return {
        width,
        height: Math.round(width / aspect),
      };
    }

    if (height != null) {
      return {
        width: Math.round(height * aspect),
        height,
      };
    }

    return {
      width: this.defaultThumbnailDimension,
      height: Math.round(this.defaultThumbnailDimension / aspect),
    };
  }

  /**
   * Make sure the image doesn’t exceed browser/device canvas limits.
   * For ios with 256 RAM and ie
   */
  // eslint-disable-next-line class-methods-use-this
  protect(image) {
    // https://stackoverflow.com/questions/6081483/maximum-size-of-a-canvas-element

    const ratio = image.width / image.height;

    const maxSquare = 5000000; // ios max canvas square
    const maxSize = 4096; // ie max canvas dimensions

    let maxW = Math.floor(Math.sqrt(maxSquare * ratio));
    let maxH = Math.floor(maxSquare / Math.sqrt(maxSquare * ratio));
    if (maxW > maxSize) {
      maxW = maxSize;
      maxH = Math.round(maxW / ratio);
    }
    if (maxH > maxSize) {
      maxH = maxSize;
      maxW = Math.round(ratio * maxH);
    }
    if (image.width > maxW) {
      const canvas = document.createElement("canvas");
      canvas.width = maxW;
      canvas.height = maxH;
      canvas.getContext("2d").drawImage(image, 0, 0, maxW, maxH);
      // eslint-disable-next-line no-param-reassign
      image = canvas;
    }

    return image;
  }

  /**
   * Resize an video to the target `width` and `height`.
   *
   * Returns a Canvas with the resized image on it.
   */
  // eslint-disable-next-line class-methods-use-this
  resizeVideo(video, targetWidth, targetHeight) {
    // eslint-disable-next-line no-param-reassign
    video.width = targetWidth;
    // eslint-disable-next-line no-param-reassign
    video.height = targetHeight;

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

    return canvas;
  }

  /**
   * Save a <canvas> element's content to a Blob object.
   *
   * @param {HTMLCanvasElement} canvas
   * @returns {Promise}
   */
  // eslint-disable-next-line class-methods-use-this
  canvasToBlob(canvas, type, quality) {
    try {
      canvas.getContext("2d").getImageData(0, 0, 1, 1);
    } catch (err) {
      if (err.code === 18) {
        return Promise.reject(new Error("cannot read image"));
      }
    }

    if (canvas.toBlob) {
      return new Promise((resolve) => {
        canvas.toBlob(resolve, type, quality);
      }).then((blob) => {
        if (blob === null) {
          throw new Error("cannot read image");
        }
        return blob;
      });
    }
    return Promise.resolve()
      .then(() => dataURItoBlob(canvas.toDataURL(type, quality), {}))
      .then((blob) => {
        if (blob === null) {
          throw new Error("could not extract blob, probably an old browser");
        }
        return blob;
      });
  }

  /**
   * Set the preview URL for a file.
   */
  setPreviewURL(fileID, preview) {
    this.uppy.setFileState(fileID, { preview });
  }

  /**
   * Set the preview data for a file.
   */
  setPreviewData(fileID, previewData) {
    this.uppy.setFileState(fileID, { previewData });
  }

  addToQueue(item) {
    this.queue.push(item);
    if (this.queueProcessing === false) {
      this.processQueue();
    }
  }

  processQueue() {
    this.queueProcessing = true;
    if (this.queue.length > 0) {
      const current = this.uppy.getFile(this.queue.shift());
      if (!current) {
        // eslint-disable-next-line max-len
        this.uppy.log(
          "[VideoThumbnailGenerator] file was removed before a thumbnail could be generated, but not removed from the queue. This is probably a bug",
          "error"
        );
        return;
      }
      // eslint-disable-next-line consistent-return
      return this.requestThumbnail(current)
        .catch(() => {})
        .then(() => this.processQueue());
    }
    this.queueProcessing = false;
    this.uppy.log("[VideoThumbnailGenerator] Emptied thumbnail queue");
    this.uppy.emit("video-thumbnail:all-generated");
  }

  requestThumbnail(file) {
    if (isVideoPreviewSupported(file.type) && !file.isRemote) {
      return this.createThumbnail(
        file,
        this.opts.thumbnailWidth,
        this.opts.thumbnailHeight
      )
        .then(({ preview, previewData }) => {
          this.setPreviewURL(file.id, preview);
          this.setPreviewData(file.id, previewData);
          this.uppy.log(
            `[VideoThumbnailGenerator] Generated thumbnail for ${file.id}`
          );
          this.uppy.emit(
            "video-thumbnail:generated",
            this.uppy.getFile(file.id),
            preview,
            previewData
          );
        })
        .catch((err) => {
          this.uppy.log(
            `[VideoThumbnailGenerator] Failed thumbnail for ${file.id}:`,
            "warning"
          );
          this.uppy.log(err, "warning");
          this.uppy.emit(
            "video-thumbnail:error",
            this.uppy.getFile(file.id),
            err
          );
        });
    }
    return Promise.resolve();
  }

  onFileAdded = (file) => {
    if (
      !file.preview &&
      file.data &&
      isVideoPreviewSupported(file.type) &&
      !file.isRemote
    ) {
      this.addToQueue(file.id);
    }
  };

  /**
   * Cancel a lazy request for a thumbnail if the thumbnail has not yet been generated.
   */
  onCancelRequest = (file) => {
    const index = this.queue.indexOf(file.id);
    if (index !== -1) {
      this.queue.splice(index, 1);
    }
  };

  /**
   * Clean up the thumbnail for a file. Cancel lazy requests and free the thumbnail URL.
   */
  onFileRemoved = (file) => {
    const index = this.queue.indexOf(file.id);
    if (index !== -1) {
      this.queue.splice(index, 1);
    }

    // Clean up object URLs.
    if (file.preview && isObjectURL(file.preview)) {
      URL.revokeObjectURL(file.preview);
    }
  };

  onRestored = () => {
    const restoredFiles = this.uppy
      .getFiles()
      .filter((file) => file.isRestored);
    restoredFiles.forEach((file) => {
      // Only add blob URLs; they are likely invalid after being restored.
      if (!file.preview || isObjectURL(file.preview)) {
        this.addToQueue(file.id);
      }
    });
  };

  waitUntilAllProcessed = (fileIDs) => {
    fileIDs.forEach((fileID) => {
      const file = this.uppy.getFile(fileID);
      this.uppy.emit("preprocess-progress", file, {
        mode: "indeterminate",
        message: this.i18n("generatingThumbnails"),
      });
    });

    const emitPreprocessCompleteForAll = () => {
      fileIDs.forEach((fileID) => {
        const file = this.uppy.getFile(fileID);
        this.uppy.emit("preprocess-complete", file);
      });
    };

    return new Promise((resolve) => {
      if (this.queueProcessing) {
        this.uppy.once("video-thumbnail:all-generated", () => {
          emitPreprocessCompleteForAll();
          resolve();
        });
      } else {
        emitPreprocessCompleteForAll();
        resolve();
      }
    });
  };

  install() {
    this.uppy.on("file-removed", this.onFileRemoved);
    if (this.opts.lazy) {
      this.uppy.on("video-thumbnail:request", this.onFileAdded);
      this.uppy.on("video-thumbnail:cancel", this.onCancelRequest);
    } else {
      this.uppy.on("file-added", this.onFileAdded);
      this.uppy.on("restored", this.onRestored);
    }

    if (this.opts.waitForThumbnailsBeforeUpload) {
      this.uppy.addPreProcessor(this.waitUntilAllProcessed);
    }
  }

  uninstall() {
    this.uppy.off("file-removed", this.onFileRemoved);
    if (this.opts.lazy) {
      this.uppy.off("video-thumbnail:request", this.onFileAdded);
      this.uppy.off("video-thumbnail:cancel", this.onCancelRequest);
    } else {
      this.uppy.off("file-added", this.onFileAdded);
      this.uppy.off("restored", this.onRestored);
    }

    if (this.opts.waitForThumbnailsBeforeUpload) {
      this.uppy.removePreProcessor(this.waitUntilAllProcessed);
    }
  }
}

export default VideoThumbnailGenerator;
